// cmp/admin/Studio.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import { baseUrl } from '../../baseurl';
axios.defaults.baseURL = baseUrl;

const Studio = () => {
  const fileInputRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);  
  const [studios, setStudios] = useState([]);
  const [filteredStudios, setFilteredStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [studioId, setStudioId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [mainStudio, setMainStudios] = useState([]);
  const [loadingBtn, setLoadingBtn] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    studio_name: '',
    start_time: '',
    end_time: '',
    description: '',
    date: '',
    main_studio_id: '',
    frame_status: 'inactive'
  });

  // Initialize with sample data
  useEffect(() => {
    loadStudios();
  }, [refresh]);

  // Filter studios when search term changes (client-side only)
  useEffect(() => {
    applySearchFilter();
  }, [searchTerm, studios]);

  const loadStudios = async (date = '') => {
    setLoading(true);
    
    try {
      const today = new Date();
      const formattedToday = today.toISOString().split("T")[0]; // "YYYY-MM-DD"
      
      // Use provided date, or current date filter, or today's date
      const filterDate = date || dateFilter || formattedToday;
      
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get(`admin/get-studio/${filterDate}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        
        setStudios(response.data.studio);
        setFilteredStudios(response.data.studio);
      } else {
        toast.error("Failed to fetch studios");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading studios");
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filter only
  const applySearchFilter = () => {
    if (!searchTerm) {
      setFilteredStudios(studios);
      return;
    }

    const filtered = studios.filter(studio =>
      studio.studio_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studio.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredStudios(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDateFilter = (e) => {
    const selectedDate = e.target.value;
    setDateFilter(selectedDate);
    
    // Reload data from server with new date filter
    if (selectedDate) {
      loadStudios(selectedDate);
    } else {
      // If date filter is cleared, load today's data
      const today = new Date().toISOString().split("T")[0];
      loadStudios(today);
      setDateFilter(today);
    }
  };

  const openAddModal = () => {
    // Set default date to today if no date filter is set
    const today = new Date().toISOString().split("T")[0];
    
    setFormData({
      studio_name: '',
      start_time: '',
      end_time: '',
      description: '',
      date: dateFilter || today,
      main_studio_id: mainStudio.length > 0 ? mainStudio[0]._id : '',
      frame_status: 'inactive'
    });
    setEditingStudio(null);
    setIsModalOpen(true);
  };

  const openEditModal = (studio) => {
    setFormData({ 
      studio_name: studio.studio_name || '',
      start_time: studio.start_time || '',
      end_time: studio.end_time || '',
      description: studio.description || '',
      date: studio.date || '',
      main_studio_id: studio.main_studio_id || (mainStudio.length > 0 ? mainStudio[0]._id : '') ,
      frame_status: studio.frame_status
    });
    setEditingStudio(studio);
    setIsModalOpen(true);
    setStudioId(studio._id);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStudio(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("vsat_token");

    // console.log(formData);
    // return false;
    
    // Validate date format (YYYY-MM-DD)
    if (formData.date && !/^\d{4}-\d{2}-\d{2}$/.test(formData.date)) {
      toast.error("Please use YYYY-MM-DD date format");
      return;
    }
    setLoadingBtn(true);
    if (editingStudio) {
      try {
        const response = await axios.put(`admin/edit-studio/${studioId}`, {
          studio_name: formData.studio_name,
          description: formData.description,
          start_time: formData.start_time,
          end_time: formData.end_time,
          date: formData.date,
          main_studio_id: formData.main_studio_id,
          frame_status: formData.frame_status
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          toast.success("Studio updated successfully");
          setRefresh(!refresh);
        } else {
          toast.error("Failed to update studio");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error updating studio");
      }
    } else {
      try {
        const response = await axios.post('admin/save-studio', {
          studio_name: formData.studio_name,
          description: formData.description,
          start_time: formData.start_time,
          end_time: formData.end_time,
          date: formData.date,
          main_studio_id: formData.main_studio_id,
          frame_status: formData.frame_status
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          toast.success("Studio saved successfully");
          setRefresh(!refresh);
        } else {
          toast.error("Failed to save studio");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error saving studio");
      }
    }
    setLoadingBtn(false);
    closeModal();
  };

  const deleteStudio = async (studioId) => {
    if (window.confirm('Are you sure you want to delete this studio session?')) {
      try {
        const token = localStorage.getItem("vsat_token");
        const response = await axios.delete(`admin/delete-studio/${studioId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          toast.success("Studio deleted successfully");
          setRefresh(!refresh);
        } else {
          toast.error("Failed to delete studio");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting studio");
      }
    }
  };

  const toggleStudioStatus = (studioId) => {
    setStudios(prev => prev.map(studio =>
      studio.id === studioId
        ? { ...studio, status: studio.status === 'active' ? 'inactive' : 'active' }
        : studio
    ));
  };

  const toggleStatus = async (studio) => {
  try {
    const token = localStorage.getItem("vsat_token");

    const newStatus = studio.status === 'active' ? 'inactive' : 'active';

    const res = await axios.put(
      `admin/update-status/${studio._id}`,
      { status: newStatus },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (res.data.success) {
      toast.success("Status updated");
      setRefresh(!refresh);
    }
  } catch (err) {
    toast.error("Error updating status");
  }
};

const toggleFrameStatus = async (studio) => {
  try {
    const token = localStorage.getItem("vsat_token");

    const newStatus = studio.frame_status === 'active' ? 'inactive' : 'active';

    const res = await axios.put(
      `admin/update-frame-status/${studio._id}`,
      { frame_status: newStatus },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (res.data.success) {
      toast.success("Frame status updated");
      setRefresh(!refresh);
    }
  } catch (err) {
    toast.error("Error updating frame status");
  }
};

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    const today = new Date().toISOString().split("T")[0];
    setDateFilter(today);
    loadStudios(today);
  };

  // Set initial date filter to today on component mount
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setDateFilter(today);
    loadMainStudios();
  }, []);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const loadMainStudios = async (date = '') => {
    
    try {
      
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/get-main-studio', {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.data.success) {
        console.log(response.data.studio);
        setMainStudios(response.data.studio);
    
      } else {
        console.log("main studio error");
      }
    } catch (error) {
      //console.log(error.response?.data?.message || "Error loading studios");
    } finally {
      //setLoading(false);
    }
  };
  
  // Handle file selection and upload
    const handleFileUpload = async (event) => {
    const token = localStorage.getItem("vsat_token");
    const file = event.target.files[0];
    if (!file) return;

    // ✅ Validate file type
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(file.type)) {
      alert("Please select a valid Excel file (.xlsx)");
      return;
    }

    setImportLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post('admin/studio-import',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Studios imported successfully!");
      } else {
        toast.error(response.data.message || "Import failed!");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Error importing studio file");
    } finally {
      setImportLoading(false);
      event.target.value = "";
    }
  };

   const exportToCSV = async () => {
    
    try {
      const token = localStorage.getItem("vsat_token");
        
      const response = await axios.get("admin/studio-export", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
  
      // Fix 3: Better blob handling with error checking
      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }
  
      // Check if it's actually an error JSON disguised as blob
      if (response.data.type === 'application/json') {
        const errorText = await response.data.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Export failed');
      }
  
      // Create blob link to download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      
      // Fix 4: Get filename from headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `studio_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      
      // Fix 5: Better error message
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          alert(`Export failed: ${errorData.error || errorData.message || 'Unknown error'}`);
        } catch {
          alert('Export failed: Server error occurred');
        }
      } else {
        alert(`Export failed: ${error.message || 'Please try again'}`);
      }
    } finally {
      
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value, // updates formData.main_studio_id
    }));
  };



  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
          <p className="text-gray-600">Manage studio sessions and schedules</p>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Session
          </button>

          <button
            onClick={handleImportClick}
            disabled={importLoading}
            className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            {importLoading ? 'Importing...' : 'Import'}
          </button>

          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Export
          </button>
          
          <a
            href={`${baseUrl}admin/download-studio-sample`}
            download
            className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Sample
          </a>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
        
      </div>

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search sessions by name or description..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={handleDateFilter}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {(searchTerm || (dateFilter && dateFilter !== new Date().toISOString().split("T")[0])) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Studios Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Frame Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredStudios.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No studio sessions found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredStudios.map((studio) => (
                  <tr key={studio._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                          {studio.studio_name?.charAt(0) || 'S'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {studio.studio_name || 'Unnamed Session'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(studio.date)}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {formatTime(studio.start_time)} - {formatTime(studio.end_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {studio.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(studio.status || 'active')}
                        <button
                          onClick={() => toggleStatus(studio)}
                          className={`p-1 rounded ${
                            studio.status === 'active' 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-red-600 hover:bg-red-100'
                          }`}
                          title={`Mark as ${studio.status === 'active' ? 'inactive' : 'active'}`}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(studio.frame_status || 'inactive')}
                        
                        <button
                          onClick={() => toggleFrameStatus(studio)}
                          className={`p-1 rounded ${
                            studio.frame_status === 'active'
                              ? 'text-green-600 hover:bg-green-100'
                              : 'text-red-600 hover:bg-red-100'
                          }`}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(studio)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteStudio(studio._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Studio Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingStudio ? 'Edit Studio Session' : 'Add New Studio Session'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Name *
                  </label>
                  <input
                    type="text"
                    name="studio_name"
                    value={formData.studio_name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter session name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Studio *
                  </label>
                  <select
                    name="main_studio_id"
                    value={formData.main_studio_id}
                    onChange={handleSelectChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter session name"
                  >
                    {
                      mainStudio.map((item,index)=>(
                        <>
                          <option value={item._id} key={index}>{item.name}</option>
                        </>
                      ))
                    }
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time *
                  </label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <input
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter session subject"
                    />
                  </div>
              </div>

              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frame Status
                  </label>
                  <select
                    name="frame_status"
                    value={formData.frame_status}
                    onChange={handleSelectChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter session name"
                  >
                    <option value="active">ACTIVE</option>
                      <option value="inactive">INACTIVE</option>
                      
                    
                  </select>
                </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {loadingBtn ? 'Wait...' : (editingStudio ? 'Update Session' : 'Add Session')}

                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Studio;